import { useContext, useEffect, useState } from "react";
import {
  createLinkBuilder,
  createPaymentBuilder,
  getCampaignById,
} from "../../apiClient/operations/paymentCollectionManagerOperations";
import useFormatDate from "../useFormatDate";
import { AlertContext } from "../../context/AlertContext";
import useSleep from "../useSleep";

export function useCampaign({ campaignId }) {
  const [campaign, setCampaign] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const { formatDate } = useFormatDate();
  const { setAlert } = useContext(AlertContext);
  const { sleep } = useSleep();

  const getCampaignData = async () => {
    try {
      setIsLoading(true);
      if (!campaignId) throw new Error("Campaingn id required");
      const res = await getCampaignById(campaignId);
      setCampaign(transformCampaign(res));
    } catch (error) {
      console.log(error);
      setAlert({
        active: true,
        type: "error",
        message: error?.message || "Error al procesar tu solicitud",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const transformCampaign = (campaign) => {
    return {
      ...campaign,
      created: formatDate(campaign?.created, "DD MMMM YYYY, h:mm a"),
      updated: formatDate(campaign?.updated, "DD MMMM YYYY, h:mm a"),
      updatedAt: new Date(campaign?.updated).getTime(),
      ...(campaign?.notifications_dates && {
        notifications_dates: campaign?.notifications_dates.map(
          (notification) => ({
            ...notification,
            date: formatDate(notification?.date, "DD MMMM YYYY, h:mm a"),
          }),
        ),
      }),
    };
  };

  const generateCampaignLinks = async () => {
    try {
      setIsPending(true);
      const now = Date.now();
      const lastUpdated = campaign?.updatedAt ?? now;
      const minutesDiff = Math.floor((now - lastUpdated) / 1000 / 60);

      if (campaign?.payment_links_status === "processing" && minutesDiff < 10) {
        setAlert({
          active: true,
          type: "warning",
          message: "Espera a que los links sean procesados",
        });
        return;
      }
      if (campaign?.payment_links_status === "processed") {
        setAlert({
          active: true,
          type: "warning",
          message: "Los links ya fueron procesados",
        });
        return;
      }
      if (
        campaign?.payment_links_status === "processing" &&
        minutesDiff >= 10
      ) {
        setAlert({
          active: true,
          type: "warning",
          message:
            "Los links están tardando más de lo esperado, se intentará procesar nuevamente",
        });
      }
      let res = await createLinkBuilder(campaign?.campaign_id);
      setCampaign((prev) => ({ ...prev, ...transformCampaign(res?.data) }));
      if (campaign?.payment_links_status === "pending") {
        setAlert({
          active: true,
          type: "success",
          message:
            "La actualización puede tardar algunos segundos, en breve estará listo",
        });
      }
      refreshCampain("links");
    } catch (error) {
      console.log(error);
      setAlert({
        active: true,
        type: "error",
        message: error?.message || "Error al procesar tu solicitud",
      });
    } finally {
      setIsPending(false);
    }
  };

  const generateCampaignNotifications = async () => {
    try {
      setIsPending(true);
      setRefresh(false);
      if (campaign?.payment_links_status === "pending") {
        setAlert({
          active: true,
          type: "warning",
          message: "Primero se deben generar los links de pago",
        });
        return;
      }
      if (campaign?.payment_links_status === "processing") {
        setAlert({
          active: true,
          type: "warning",
          message: "Espera a que los links sean procesados",
        });
        return;
      }
      if (campaign?.notifications_status === "processing") {
        setAlert({
          active: true,
          type: "warning",
          message: "Espera a que las notificaciones sean procesadas",
        });
        return;
      }
      if (campaign?.notifications_status === "processed") {
        setAlert({
          active: true,
          type: "warning",
          message: "Las notificaciones ya fueron procesadas",
        });
        return;
      }
      const res = await createPaymentBuilder(campaign?.campaign_id);
      setCampaign((prev) => ({ ...prev, ...transformCampaign(res?.data) }));
      setAlert({
        active: true,
        type: "success",
        message:
          "La actualización puede tardar algunos segundos, en breve estará listo",
      });
      refreshCampain("notifications");
    } catch (error) {
      console.log(error);
      setAlert({
        active: true,
        type: "error",
        message: error?.message || "Error al procesar tu solicitud",
      });
    } finally {
      setIsPending(false);
    }
  };

  const refreshCampain = async (condition) => {
    let retries = 0;
    let isDataProcessed = false;
    let res = null;

    while (retries < 7 && !isDataProcessed) {
      await sleep(10000);
      res = await getCampaignById(campaignId);
      isDataProcessed =
        condition === "links"
          ? res?.payment_links_status === "processed"
          : res?.notifications_status === "processed";
      retries++;
    }

    if (isDataProcessed) {
      setCampaign(transformCampaign(res));
      if (condition === "notifications") {
        setRefresh(true);
      }
      setAlert({
        active: true,
        type: "success",
        message:
          condition === "links"
            ? "Links de pago procesados correctamente"
            : "Notificaciones procesadas correctamente",
      });
    }
  };

  useEffect(() => {
    getCampaignData();
  }, [campaignId]);

  return {
    campaign,
    generateCampaignLinks,
    generateCampaignNotifications,
    isLoading,
    isPending,
    refresh,
  };
}
